import React from 'react';

import { SpotInterface } from 'types';
import { isActiveTab } from 'utils/helpers';
import { canViewLocalIntelligence } from 'containers/LocalIntelligence/utils';
import { AuthorizedView, Roles } from 'components/Utilities/AuthorizedView';
// Icons
import BarChartIcon from 'assets/icons/barChart.svg?react';
import VideoCameraIcon from 'assets/icons/videoCamera.svg?react';
import PathIcon from 'assets/icons/path.svg?react';
import ChartBoardIcon from 'assets/icons/chartBoard.svg?react';
import TicketIcon from 'assets/icons/ticket.svg?react';
import PersonIcon from 'assets/icons/person2.svg?react';
import WarningIcon from 'assets/icons/warningTriangle.svg?react';
import CalendarIcon from 'assets/icons/calendar.svg?react';
import EventTicketIcon from 'assets/icons/eventTicket.svg?react';
import GridIcon from 'assets/icons/grid.svg?react';
import TagIcon from 'assets/icons/tag.svg?react';
import PropertyIcon from 'assets/icons/property.svg?react';
import CarIcon from 'components/Icons/CarIcon';
import LocationIcon from 'components/Icons/LocationIcon';

import SidebarSection from './SidebarSection';
import SidebarItem from './SidebarItem';

type SpotMenuProps = {
    selectedSpot: SpotInterface;
    baseUrl: string;
    isExpanded: boolean;
    navigateToTab: (tabUrl: string) => void;
};

function SpotMenu({ selectedSpot: spot, baseUrl, navigateToTab, isExpanded }: SpotMenuProps): JSX.Element {
    const hasRentals = !!spot.price_hourly || !!spot.price_monthly || !!spot.price_daily;

    return (
        <>
            <SidebarSection title="Asset Insights" isExpanded={isExpanded}>
                {hasRentals && (
                    <>
                        {spot.spot_config.show_revenue_page && (
                            <AuthorizedView allowed={[Roles.Superuser, Roles.Admin]}>
                                <SidebarItem
                                    label="Revenue"
                                    icon={<BarChartIcon />}
                                    active={isActiveTab('revenue')}
                                    isExpanded={isExpanded}
                                    onClick={() => navigateToTab(`${baseUrl}revenue`)}
                                />
                            </AuthorizedView>
                        )}
                        <AuthorizedView allowed={[Roles.Superuser, Roles.Admin]}>
                            <SidebarItem
                                label="Property Insights"
                                icon={<PropertyIcon />}
                                active={isActiveTab('property-insights')}
                                isExpanded={isExpanded}
                                onClick={() => navigateToTab(`${baseUrl}property-insights`)}
                            />
                        </AuthorizedView>
                        <AuthorizedView allowed={[Roles.Superuser, Roles.Admin]}>
                            <SidebarItem
                                label="Visibility"
                                icon={<VideoCameraIcon />}
                                active={isActiveTab('visibility')}
                                isExpanded={isExpanded}
                                onClick={() => navigateToTab(`${baseUrl}visibility`)}
                            />
                        </AuthorizedView>
                    </>
                )}
                {canViewLocalIntelligence(spot) && (
                    <AuthorizedView allowed={[Roles.Superuser, Roles.Admin, Roles.Employee]}>
                        <SidebarItem
                            label="Local Intelligence"
                            icon={<LocationIcon />}
                            active={isActiveTab('local-intelligence')}
                            isExpanded={isExpanded}
                            onClick={() => navigateToTab(`${baseUrl}local-intelligence`)}
                        />
                    </AuthorizedView>
                )}

                {hasRentals && (
                    <>
                        {spot.spot_config.show_timeline && (
                            <AuthorizedView allowed={[Roles.Superuser, Roles.Admin, Roles.Employee]}>
                                <SidebarItem
                                    label="Timeline"
                                    icon={<PathIcon />}
                                    active={isActiveTab('timeline')}
                                    isExpanded={isExpanded}
                                    onClick={() => navigateToTab(`${baseUrl}timeline`)}
                                />
                            </AuthorizedView>
                        )}
                        <AuthorizedView allowed={[Roles.Superuser, Roles.Admin]}>
                            <SidebarItem
                                label="Reports"
                                icon={<ChartBoardIcon />}
                                active={isActiveTab('reports')}
                                isExpanded={isExpanded}
                                onClick={() => navigateToTab(`${baseUrl}reports`)}
                            />
                        </AuthorizedView>
                    </>
                )}
                {spot.classification === 'Airport Lot' && (
                    <AuthorizedView allowed={[Roles.Superuser, Roles.Admin, Roles.Employee]}>
                        <SidebarItem
                            label="Aggregator Rentals"
                            icon={<GridIcon />}
                            active={isActiveTab('aggregator-rentals')}
                            isExpanded={isExpanded}
                            onClick={() => navigateToTab(`${baseUrl}aggregator-rentals`)}
                        />
                    </AuthorizedView>
                )}
            </SidebarSection>

            <SidebarSection title="Property Controls" isExpanded={isExpanded}>
                <AuthorizedView allowed={[Roles.Superuser, Roles.Admin, Roles.Employee]}>
                    <SidebarItem
                        label="Validation Codes"
                        icon={<TicketIcon />}
                        active={isActiveTab('codes')}
                        isExpanded={isExpanded}
                        onClick={() => navigateToTab(`${baseUrl}codes`)}
                    />
                </AuthorizedView>
                {hasRentals && (
                    <AuthorizedView allowed={[Roles.Superuser, Roles.Admin, Roles.Employee]}>
                        <SidebarItem
                            label="Discount Codes"
                            icon={<TagIcon />}
                            active={isActiveTab('promos')}
                            isExpanded={isExpanded}
                            onClick={() => navigateToTab(`${baseUrl}promos`)}
                        />
                    </AuthorizedView>
                )}
                <AuthorizedView allowed={[Roles.Superuser, Roles.Admin, Roles.Employee]}>
                    <SidebarItem
                        label="Visitors"
                        icon={<PersonIcon />}
                        active={isActiveTab('visitors')}
                        isExpanded={isExpanded}
                        onClick={() => navigateToTab(`${baseUrl}visitors`)}
                    />
                </AuthorizedView>
                <AuthorizedView allowed={[Roles.Superuser, Roles.Admin, Roles.Employee]}>
                    <SidebarItem
                        label="Drivers"
                        icon={<CarIcon />}
                        active={isActiveTab('drivers')}
                        isExpanded={isExpanded}
                        onClick={() => navigateToTab(`${baseUrl}drivers`)}
                    />
                </AuthorizedView>
                {hasRentals && (
                    <>
                        <AuthorizedView allowed={[Roles.Superuser, Roles.Admin, Roles.Employee]}>
                            <SidebarItem
                                label="Waive a Violation"
                                icon={<WarningIcon />}
                                active={isActiveTab('violations')}
                                isExpanded={isExpanded}
                                onClick={() => navigateToTab(`${baseUrl}violations`)}
                            />
                        </AuthorizedView>
                        <AuthorizedView allowed={[Roles.Superuser, Roles.Admin, Roles.Employee]}>
                            <SidebarItem
                                label="Event Calendar"
                                icon={<EventTicketIcon />}
                                active={isActiveTab('events')}
                                isExpanded={isExpanded}
                                onClick={() => navigateToTab(`${baseUrl}events`)}
                            />
                        </AuthorizedView>
                    </>
                )}
                <AuthorizedView allowed={[Roles.Superuser]}>
                    <SidebarItem
                        label="Rate Calendar"
                        icon={<CalendarIcon />}
                        active={isActiveTab('rates')}
                        isExpanded={isExpanded}
                        onClick={() => navigateToTab(`${baseUrl}rates`)}
                    />
                </AuthorizedView>
            </SidebarSection>
        </>
    );
}

export default SpotMenu;
